import { Alert, Box, Divider, Paper, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useState } from "react";
import { ReadOnlyMetadataField } from "../../../components/UIToolkit/molecules/ReadOnlyMetadataField";
import { SftpCredentialFields, type SftpCredentials, useCopyAllSftpCredentials } from "../../../components/UIToolkit/molecules/SftpCredentialFields";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";

function buildShareUrl(linkToken: string): string {
	return `${window.location.origin}/link?link_token=${linkToken}`;
}

export function SftpProvisioningSection() {
	const { showSnackbar } = useSnackbar();
	const copyAllCredentials = useCopyAllSftpCredentials();
	const [clientId, setClientId] = useState("");
	const [credentials, setCredentials] = useState<SftpCredentials | null>(null);
	const [shareUrl, setShareUrl] = useState<string | null>(null);

	const { mutate: provision, isPending } = useMutation({
		mutationFn: async (id: string) => {
			const response = await backendService.createSftpUser(id);
			return response.data;
		},
		onSuccess: (data) => {
			setCredentials(data);
			setClientId("");
		},
		onError: (error) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			showSnackbar?.(axiosError.response?.data?.detail ?? "Failed to create SFTP user", "error");
		},
	});

	const { mutate: generateLink, isPending: isLinkPending } = useMutation({
		mutationFn: async () => {
			const response = await backendService.createSftpShareLink();
			return response.data;
		},
		onSuccess: (data) => {
			setShareUrl(buildShareUrl(data.link_token));
		},
		onError: (error) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			showSnackbar?.(axiosError.response?.data?.detail ?? "Failed to generate share link", "error");
		},
	});

	const handleSubmit = () => {
		const trimmed = clientId.trim();
		if (!trimmed) {
			showSnackbar?.("Client ID is required", "error");
			return;
		}
		setCredentials(null);
		provision(trimmed);
	};

	return (
		<Paper sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
			{/* Create directly */}
			<Box>
				<Typography variant="h5" sx={{ mb: 2 }}>
					New SFTP User
				</Typography>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
					<TextField
						label="Client ID"
						value={clientId}
						onChange={(e) => setClientId(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
						fullWidth
						disabled={isPending}
					/>
					<SystemButton onClick={handleSubmit} loading={isPending} disabled={!clientId.trim()}>
						Create SFTP User
					</SystemButton>
				</Box>

				{credentials && (
					<Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
						<Alert severity="success">SFTP user created. Save these credentials — the password will not be shown again.</Alert>
						<SftpCredentialFields credentials={credentials} dataTestId="sftp-provisioning-credentials" />
						<SystemButton onClick={() => copyAllCredentials(credentials)}>Copy All</SystemButton>
					</Box>
				)}
			</Box>
			<Divider />
			{/* Shareable link */}
			<Box>
				<Typography variant="h6" sx={{ mb: 0.5 }}>
					Shareable Link
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						mb: 2,
					}}
				>
					Generate a link (valid 7 days) that lets anyone provision an SFTP user without a support account.
				</Typography>
				<SystemButton onClick={() => generateLink()} loading={isLinkPending} sx={{ maxWidth: 240 }}>
					Generate Link
				</SystemButton>

				{shareUrl && (
					<Box sx={{ mt: 2, maxWidth: 480 }}>
						<ReadOnlyMetadataField label="Shareable link" value={shareUrl} copyable dataTestId="sftp-share-link" />
					</Box>
				)}
			</Box>
		</Paper>
	);
}
